import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $insertNodes,
  CLEAR_HISTORY_COMMAND,
  SerializedEditorState,
  SerializedLexicalNode,
} from "lexical";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { $generateNodesFromDOM } from "@lexical/html";

interface LoadInitialValuePluginProps {
  htmlString?: string;
  jsonString?: string;
  inferString?: string;
}

export interface LoadInitialValuePluginRef {
  reset: (props?: LoadInitialValuePluginProps) => void;
}

const LoadInitialValuePlugin = forwardRef<
  LoadInitialValuePluginRef,
  LoadInitialValuePluginProps
>(({ htmlString, jsonString, inferString }, ref) => {
  const [editor] = useLexicalComposerContext();
  const [applied, setApplied] = useState(false);

  function setValues({
    htmlString,
    jsonString,
    inferString,
  }: LoadInitialValuePluginProps) {
    if (!editor || (!htmlString && !jsonString && !inferString) || applied) {
      return;
    }
    const _jsonString =
      jsonString || (isJsonString(inferString) ? inferString : undefined);
    const _htmlString =
      htmlString || (isHtmlString(inferString) ? inferString : undefined);

    editor.update(() => {
      const parser = new DOMParser();
      if (_jsonString) {
        const json: SerializedEditorState<SerializedLexicalNode> =
          JSON.parse(_jsonString);
        const editorState = editor.parseEditorState(json);
        editor.setEditorState(editorState);
        setApplied(true);
        return;
      }
      if (_htmlString) {
        const dom = parser.parseFromString(_htmlString, "text/html");

        // Once you have the DOM instance it's easy to generate LexicalNodes.
        const nodes = $generateNodesFromDOM(editor, dom);

        // Select the root
        $getRoot().select();

        // Insert them at a selection.
        $insertNodes(nodes);
        setApplied(true);
        return;
      }
    });
  }

  function reset(values?: LoadInitialValuePluginProps) {
    if (!values) {
      editor.update(() => {
        const parser = new DOMParser();

        const dom = parser.parseFromString("", "text/html");

        // Once you have the DOM instance it's easy to generate LexicalNodes.
        const nodes = $generateNodesFromDOM(editor, dom);

        // Select the root
        $getRoot().select();

        // Insert them at a selection.
        $insertNodes(nodes);
        setApplied(true);
        return;
      });
      return;
    }
    setValues(values);
  }

  useEffect(() => {
    if (applied) {
      return;
    }
    setValues({
      htmlString,
      jsonString,
      inferString,
    });
  }, [editor, htmlString, jsonString, inferString]);

  useImperativeHandle(ref, () => ({
    reset,
  }));

  useEffect(() => {
    if (editor) {
      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
    }
  }, [inferString, htmlString, jsonString]);

  return null;
});

export default LoadInitialValuePlugin;

function isJsonString(text?: string) {
  return (
    typeof text === "string" && text?.startsWith("{") && text?.endsWith("}")
  );
}
function isHtmlString(text?: string) {
  return !isJsonString(text) && typeof text === "string";
}
